/** The current `app-storage` shape; bump it together with a new branch below. */
export const APP_STORAGE_VERSION = 8;

/**
 * `app-storage` migrations, one branch per version bump. Kept out of
 * `useApp.ts`, which pulls in Apollo and i18n, so they are unit-testable.
 */
export const migrateAppStorage = (persistedState: unknown, version: number) => {
  const s = persistedState as {
    paywallBTimer?: number;
    paywallBTimerEndsAt?: number | null;
    paywallDarkTimerEndsAt?: number | null;
    initialPaywallDismissed?: boolean;
    subtitlesPreference?: string | null;
    audioPreference?: string | null;
    history?: Record<
      string,
      {
        lastEpisodeIndex?: number;
        lastEpisodeSlug?: string;
        nextEpisodeId?: string;
      }
    >;
  };
  if (version < 2 && s.paywallBTimerEndsAt == null) {
    if (typeof s.paywallBTimer === "number" && s.paywallBTimer > 0) {
      s.paywallBTimerEndsAt = Date.now() + s.paywallBTimer * 1000;
    } else if (typeof s.paywallBTimer === "number" && s.paywallBTimer <= 0) {
      s.paywallBTimerEndsAt = Date.now() - 1;
    } else {
      s.paywallBTimerEndsAt = null;
    }
    delete s.paywallBTimer;
  }
  if (version < 3 && typeof s.initialPaywallDismissed !== "boolean") {
    s.initialPaywallDismissed = false;
  }
  if (version < 4) {
    s.paywallDarkTimerEndsAt = s.paywallBTimerEndsAt ?? null;
    delete s.paywallBTimerEndsAt;
  }
  // v5 added `locale`, which is optional (undefined = detect), so there
  // is no shape to reshape — every branch above is a real migration.
  if (version < 6 && s.history) {
    // The entry now holds the episode to resume on, not the one that was
    // watched. Carrying the old value over resumes one episode earlier
    // than it should, once, rather than losing the history outright.
    for (const entry of Object.values(s.history)) {
      if (entry.lastEpisodeSlug) entry.nextEpisodeId = entry.lastEpisodeSlug;
      delete entry.lastEpisodeSlug;
      delete entry.lastEpisodeIndex;
    }
  }
  if (version < 7) {
    // Track preferences moved from the English display name ("French")
    // to the BCP-47 code, which the old value can't be mapped back to.
    // Dropping them puts the user on the locale-aligned default — the
    // new intended behaviour anyway. "Off" is the one signal worth
    // carrying over.
    s.subtitlesPreference = s.subtitlesPreference === "Off" ? "off" : null;
    s.audioPreference = null;
  }
  if (version < 8) {
    // The offer countdown lives in memory since SHO-765 (useAppState): it is
    // scoped to the page session, so a persisted deadline would outlive the
    // reload that is meant to restart it.
    delete s.paywallDarkTimerEndsAt;
  }
  // `playbackSpeed` (SHO-815) needed no bump either: like `locale`, an older
  // state without it reads the initial `1` through persist's merge.
  return persistedState;
};
